import { getApps, initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';

/**
 * Firebase app aligned with trove-extension service worker auth (`trovepile-39142`).
 * Used only for MFA reCAPTCHA in the embedded extension SPA — MFA session lives in the SW.
 * Config mirrors trove-extension/src/config/firebase-config.ts.
 */
const SW_ALIGNED_APP_NAME = 'trove-sw-aligned-recaptcha';

const AUTH_EMULATOR_URL = 'http://localhost:9099';

const productionFirebaseConfig = {
  apiKey: 'AIzaSyAih6uQrRHigGORdAiUJcdGRzyINh-EVL8',
  authDomain: 'trovepile-39142.firebaseapp.com',
  projectId: 'trovepile-39142',
  storageBucket: 'trovepile-39142.appspot.com',
  messagingSenderId: '887729215749',
  appId: '1:887729215749:web:adac43e585751a75b07f6c',
  measurementId: 'G-SCTQBQJ4Q0',
};

/**
 * Match trove-extension SW auth (firebase-config useFirebaseAuthEmulator).
 *
 * Gate on `MODE`, NOT `import.meta.env.PROD`: the embedded extension dev build
 * runs `vite build --mode development` which leaves PROD === true, so a PROD-gated
 * check silently pointed MFA at production while the SW used the emulator. `MODE`
 * is the reliable discriminator (production build => 'production'). `isChromeExtensionPage`
 * must NOT enable the emulator on its own — it is true for the production extension
 * build too.
 */
const useExtensionAuthEmulator =
  import.meta.env.MODE !== 'production' &&
  import.meta.env.VITE_EXTENSION_SW_AUTH_EMULATOR !== 'false' &&
  (import.meta.env.MODE === 'development' ||
    import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true');

const extensionSwFirebaseConfig = useExtensionAuthEmulator
  ? { ...productionFirebaseConfig, authDomain: 'localhost:9099' }
  : productionFirebaseConfig;

let swAlignedAuth: Auth | null = null;

/** True when embedded extension auth targets the local Auth emulator. */
export function isExtensionAuthEmulatorEnabled(): boolean {
  return useExtensionAuthEmulator;
}

function connectSwAlignedAuthEmulator(auth: Auth): void {
  try {
    connectAuthEmulator(auth, AUTH_EMULATOR_URL, { disableWarnings: true });
  } catch {
    // already connected
  }
  // RecaptchaVerifier still calls getRecaptchaConfig unless this is set (emulator returns 501).
  auth.settings.appVerificationDisabledForTesting = true;
}

export function getSwAlignedAuth(): Auth {
  if (swAlignedAuth) return swAlignedAuth;

  const existing = getApps().find((a) => a.name === SW_ALIGNED_APP_NAME);
  const app = existing ?? initializeApp(extensionSwFirebaseConfig, SW_ALIGNED_APP_NAME);
  swAlignedAuth = getAuth(app);

  if (useExtensionAuthEmulator) {
    connectSwAlignedAuthEmulator(swAlignedAuth);
  }

  return swAlignedAuth;
}
